import { observable, runInAction } from 'mobx';
import { Storage } from 'src/storage';
import type { DeviceDto } from '@emfit/shared-types';

type WireEvent = {
  event: string;
  data?: unknown;
};

class WireService {
  private ws: WebSocket | null = null;
  private url: string;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private refCount = 0;
  private reconnectDelay = 1000;

  deviceStatus: {
    [deviceId: string]: DeviceDto;
  } = observable({});

  /**
   * Whether status is flowing, for screens that must not look live when they
   * are not. `lastMessageAt` counts heartbeats too: a quiet ward still answers.
   */
  link = observable({
    state: 'closed' as 'closed' | 'connecting' | 'live' | 'reconnecting',
    lastMessageAt: 0,
  });

  private setLink(state: WireService['link']['state']) {
    runInAction(() => {
      this.link.state = state;
    });
  }

  constructor(url: string) {
    this.url = url;
  }

  connect() {
    this.refCount++;
    if (this.refCount > 1) return; // already open or connecting
    this._open();
  }

  disconnect() {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0) return; // other consumers still active
    this._close();
  }

  /**
   * Drops the socket and every status held for the login that is ending. The
   * status map is keyed by physical device id, so without this it would carry
   * one account's device states into the next.
   */
  reset() {
    this.refCount = 0;
    this.reconnectDelay = 1000;
    this._close();
    runInAction(() => {
      for (const key of Object.keys(this.deviceStatus)) {
        delete this.deviceStatus[key];
      }
    });
  }

  /**
   * Force a fresh socket now, skipping any pending backoff. For the refresh
   * control: a user asking for current data should not wait out a 30 s step.
   * Consumer count is untouched, so this neither opens nor closes on its own.
   */
  reconnect() {
    this.reconnectDelay = 1000;
    this._close();
    if (this.refCount > 0) this._open();
  }

  private _open() {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    // Cancel any pending reconnect — we're opening now
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.link.state !== 'reconnecting') this.setLink('connecting');
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.reconnectDelay = 1000; // reset backoff on successful connect
      this.setLink('live');
      const token = Storage.get('token');
      this.ws!.send(
        JSON.stringify({ event: 'status-updates', data: { token } })
      );
      this.ticker = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ event: 'isAlive' }));
        }
      }, 30000);
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WireEvent;
        this.handleMessage(message);
      } catch (error) {
        console.error('Wire: Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('Wire: WebSocket error:', error);
    };

    this.ws.onclose = () => {
      if (this.ticker) {
        clearInterval(this.ticker);
        this.ticker = null;
      }
      // Only reconnect if there are still active consumers
      if (this.refCount > 0) {
        this.setLink('reconnecting');
        this.scheduleReconnect();
      } else {
        this.setLink('closed');
      }
    };
  }

  private _close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    if (this.ws) {
      // Null handlers before close so onclose does not fire and schedule a reconnect
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.setLink('closed');
  }

  private handleMessage(message: WireEvent) {
    runInAction(() => {
      this.link.lastMessageAt = Date.now();
    });
    switch (message.event) {
      case 'status-updates':
      case 'status-updates-all': {
        const statusUpdate = message.data as DeviceDto;
        runInAction(() => {
          this.deviceStatus[statusUpdate.id] = statusUpdate;
        });
        break;
      }
      case 'isAlive':
      case 'heartbeat':
        break;
      default:
        console.log('Wire: Unknown message:', message);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    console.log(`Wire: Reconnecting in ${this.reconnectDelay}ms...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.refCount > 0) {
        this._open();
      }
    }, this.reconnectDelay);
    // Exponential backoff capped at 30s
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }
}

const wsUrl = import.meta.env.VITE_CARE_WS_URL || 'wss://care-api.emfit.com';
const Wire = new WireService(wsUrl);

export { Wire };
